import { Button, Card, Collapse, Flex, Text, theme } from '@/components';
import {
  type ProjectSearchFormType,
  initialValueOfProjectSearchForm,
} from '@/features/search/_models/projects-search';
import {
  CloseOutlined,
  DownOutlined,
  PlusOutlined,
  RightOutlined,
} from '@ant-design/icons';
import type { FC } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useCurrentFiscalYear } from '../../_hooks/get-current-fiscal-year';
import { useProjectsSearchStore } from '../../_stores/projects-search-store';
import { AnalyticSearchFields } from './fields/amount-search-fields/AnalyticSearchFields';

const KEY = 'analysis-trend-search';

export const AmountSearch: FC = () => {
  const { token } = theme.useToken();
  const { openAmountAccordion, setOpenAmountAccordion } =
    useProjectsSearchStore();
  const { control } = useFormContext<ProjectSearchFormType>();
  const currentFiscalYear = useCurrentFiscalYear();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'formulaConditions',
  });

  const { t } = useTranslation();

  const sheetType = useWatch({
    control,
    name: 'sheetType',
  });

  if (sheetType !== 'RS') {
    return null;
  }

  return (
    <Collapse
      activeKey={openAmountAccordion ? KEY : undefined}
      expandIcon={({ isActive }) =>
        isActive ? (
          <DownOutlined style={{ color: token.colorPrimary, fontSize: 12 }} />
        ) : (
          <RightOutlined style={{ color: token.colorPrimary, fontSize: 12 }} />
        )
      }
      onChange={(e) => {
        setOpenAmountAccordion(!!e.length);
      }}
      style={{ borderRadius: 0 }}
      items={[
        {
          id: 'analysis-trend-search',
          label: <Text color="primary" text={t('analysis-trend-search')} />,
          key: KEY,
          style: {
            borderRadius: 0,
            background: token.colorBorderBg,
          },
          children: (
            <Flex vertical gap={16}>
              {fields.map((field, index) => (
                <Card
                  key={field.id}
                  style={{
                    borderRadius: '8px',
                    position: 'relative',
                  }}
                  styles={{ body: { paddingBottom: 0 } }}
                >
                  {fields.length > 1 && (
                    <CloseOutlined
                      onClick={() => remove(index)}
                      style={{
                        position: 'absolute',
                        top: '12px',
                        right: '12px',
                        zIndex: 1,
                        fontSize: '14px',
                        color: token.colorPrimary,
                        cursor: 'pointer',
                      }}
                    />
                  )}
                  <AnalyticSearchFields index={index} />
                </Card>
              ))}
              {fields.length < 3 && (
                <Button
                  type="default"
                  icon={<PlusOutlined />}
                  onClick={() =>
                    append(
                      initialValueOfProjectSearchForm(
                        currentFiscalYear.data ??
                          currentFiscalYear.basicFiscalYear,
                      ).formulaConditions,
                    )
                  }
                  disabled={fields.length >= 3}
                >
                  {t('amount-search-add-condition')}
                </Button>
              )}
            </Flex>
          ),
        },
      ]}
    />
  );
};
